import React, { FC, MutableRefObject, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import { DrugItem, Filter, SelectedFilter } from 'src/api/types';
import { defaultPageSize } from 'src/api/requests/postDrugSearch';
import Tabs, { Tab } from 'src/shared-components/Tabs/Tabs';
import { IS_VISIBLE_HANDBOOK_FEATURE } from 'src/utils/constants';
import isEmpty from 'lodash/isEmpty';
import { GeneratedLinkUrlObject, SearchPageQueryType } from 'src/utils/links';
import { DrugCardVariant } from 'src/shared-components/DrugCard/types';
import dynamic from 'next/dynamic';
import SkeletonLoaders from 'src/shared-components/Loaders/SkeletonLoaders';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import NextImage from 'src/shared-components/NextImage/NextImage';
import Button, {
  ButtonColor,
  ButtonIcon,
  ButtonVariant
} from 'src/shared-components/Button_v3';
import LoadingButton from 'src/shared-components/design-system/LoadingButton';
import { delayWrapper } from 'src/utils/delayWrapper';
import Skeleton from 'src/shared-components/Loaders/Skeleton';
import trns from '../trns';
import buttonTrns from '../../../shared-components/Button/trns';
import postSearchLog from '../../../api/requests/postSearchLog';
import TradenameCategoryBannerBanner from './TradenameCategoryBanner';

const DrugCard = dynamic(
  () =>
    delayWrapper<typeof DrugCard>(
      import('src/shared-components/DrugCard/DrugCard')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={120} />
  }
);

const SpinnerLoader = dynamic(
  () => import('src/shared-components/Loaders/SpinnerLoader'),
  {
    ssr: false
  }
);

const emptyBoxIcon = 'tradename/empty-box.svg';

interface Props {
  filters: Filter[];
  selectedFilters: SelectedFilter[];
  drugList: DrugItem[];
  gtagPrefix?: GTAGPrefix;
  totalDrugsCount: number;
  lastPageReached: boolean;
  isLoading: boolean;
  selectedPages: number[];
  handleLoadMoreSubmit: (page: number) => void;
  isAnalogBannerVisible?: boolean;
  isTradenameBannerVisible?: boolean;
  textSearch?: string;
  pageLinkGenerationMethod: (
    query: SearchPageQueryType
  ) => GeneratedLinkUrlObject;
  analogsRef?: MutableRefObject<HTMLDivElement>;
}

const tabList: Tab[] = [
  {
    id: 'description',
    title: <FormattedMessage {...trns.descriptionTab} />
  },
  {
    id: 'sertificates',
    title: <FormattedMessage {...trns.sertificatesTab} />
  },
  {
    id: 'analogs',
    title: <FormattedMessage {...trns.analogsTab} />
  },
  {
    id: 'diagnoses',
    title: <FormattedMessage {...trns.diagnosesTab} />
  },
  {
    id: 'reviews',
    title: <FormattedMessage {...trns.reviewsTab} />
  }
];

const SearchResultsContainer: FC<Props> = ({
  drugList,
  gtagPrefix,
  totalDrugsCount,
  lastPageReached,
  isLoading,
  selectedPages,
  handleLoadMoreSubmit,
  isAnalogBannerVisible,
  isTradenameBannerVisible,
  textSearch,
  analogsRef
}) => {
  const { sendSelectItemAnalytic } = useAnalytics();
  const { trackGtagEvent } = useGTAG();

  const handleSendAnalytic = useCallback(
    (drug: DrugItem) => () => {
      setTimeout(() => {
        postSearchLog({
          searchQuery: textSearch,
          searchDrugNames: drugList.map((item) => item.name),
          userChoiceDrugName: drug.name
        });
        sendSelectItemAnalytic(drug);
      }, 250);
    },
    [textSearch, JSON.stringify(drugList)]
  );

  const handleLoadMore = () => {
    handleLoadMoreSubmit(selectedPages[selectedPages?.length - 1] + 1);
  };

  // Hide `load more` button if all of the cards are displayed already
  const showLoadMoreCard =
    !lastPageReached && totalDrugsCount >= defaultPageSize;

  const skeletonContainerClassName =
    'col-span-2 compendium-max-h-110 overflow-hidden border-r border-l border-white';

  const handleScrollToAnalogs = () => {
    analogsRef?.current?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      trackGtagEvent(
        `${gtagPrefix}_banner_see_analogues`,
        textSearch,
        'tradename_name'
      );
    }, 250);
  };

  const drugListToRender = useMemo(() => {
    const newArr = [...drugList];
    if (isTradenameBannerVisible) {
      // Modify drugList with tradename banners injection
      newArr.splice(4, 0, { isTradenameBanner: true } as unknown as DrugItem);
      newArr.splice(2, 0, { isTradenameBanner: true } as unknown as DrugItem);
    }
    return newArr;
  }, [JSON.stringify(drugList), isTradenameBannerVisible]);

  return (
    <div
      className="flex flex-col w-full px-4 lm:px-0"
      data-cy="search_results_container"
    >
      {isAnalogBannerVisible && (
        <div className="w-full bg-gradient-to-r from-bahamaBlue to-pictonBlue rounded-xl flex flex-col align-center  m:flex-row justify-center p-6 m:px-20 m:py-8 mt-4">
          <div className="flex justify-center m:justify-start mr-8">
            <NextImage
              alt="emptyBox"
              src={emptyBoxIcon}
              layout="fixed"
              height={130}
              width={130}
            />
          </div>
          <div className="flex flex-col align-center justify-center m:justify-start text-white space-y-4  text-center m:text-start">
            <p className="fs-header6-bold">
              <FormattedMessage
                {...trns.notInStock}
                values={{
                  drug: textSearch
                }}
              />
            </p>
            <p className="fs-body-lg-bold">
              <FormattedMessage {...trns.notLeftWithout} />
            </p>
            <div className="w-auto flex justify-center m:justify-start">
              <Button
                onClick={handleScrollToAnalogs}
                color={ButtonColor.gray}
                variant={ButtonVariant.regular}
                icon={{ variant: ButtonIcon.arrowIcon, alt: 'arrowIcon' }}
                buttonLabel={
                  <p className="text-black fs-body-bold">
                    <FormattedMessage {...trns.viewAnalogues} />
                  </p>
                }
              />
            </div>
          </div>
        </div>
      )}
      {!IS_VISIBLE_HANDBOOK_FEATURE && <Tabs tabList={tabList} />}
      <div
        className={cn(
          'w-full grid grid-cols-4 pt-5 lm:pt-8 bg-white z-10 relative',
          's:grid-cols-4 m:grid-cols-8 lm:grid-cols-2 l:grid-cols-8 xl:px-0'
        )}
      >
        {drugListToRender.map((drug, index) => {
          if ('isTradenameBanner' in drug) {
            return (
              <TradenameCategoryBannerBanner
                key={`${drug.translitName}_${index}-banner`}
                className={cn(
                  'col-span-4 m:col-span-8 lm:col-span-2 l:col-span-8',
                  // Display next to second drug card for mobile
                  index === 2 && 'flex m:hidden lm:flex l:hidden',
                  // Display next to fourth drug card for desktop
                  index === 5 && 'hidden m:flex'
                )}
              />
            );
          }
          return (
            <div
              key={`${drug.translitName}_${index}`}
              className="col-span-2 compendium-max-h-110"
            >
              <DrugCard
                drug={drug}
                variant={DrugCardVariant.SearchCard}
                gtagPrefix={gtagPrefix}
                containerClassName="h-[100%]"
                isPriorityLoading={index <= 1 && true}
                onClick={() => handleSendAnalytic(drug)}
              />
            </div>
          );
        })}
        {isLoading && isEmpty(drugList) && (
          <SkeletonLoaders
            loaderList={[
              {
                height: 408,
                width: '100%',
                containerClassName: skeletonContainerClassName
              },
              {
                height: 408,
                width: '100%',
                containerClassName: skeletonContainerClassName
              },
              {
                height: 408,
                width: '100%',
                containerClassName: skeletonContainerClassName
              },
              {
                height: 408,
                width: '100%',
                containerClassName: skeletonContainerClassName
              }
            ]}
          />
        )}
        {isLoading && !isEmpty(drugList) && <SpinnerLoader />}
      </div>
      {showLoadMoreCard && (
        <div className="mt-9">
          <LoadingButton
            buttonLabel={<FormattedMessage {...buttonTrns.loadMore} />}
            onClick={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
};

export default SearchResultsContainer;
